import React from 'react';
import { cx } from './clases';

export type TonoBarra = 'primario' | 'exito' | 'error' | 'aviso';

const TONO: Record<TonoBarra, string> = {
  primario: 'bg-metas-primario',
  exito: 'bg-metas-exito',
  error: 'bg-metas-error',
  aviso: 'bg-metas-campo-aviso',
};

interface Props {
  /** Avance como FRACCIÓN (0.821 = 82.1 %). `null` = no hay contra qué medir. */
  fraccion: number | null;
  /** Nombre accesible: «Suma capturada contra la meta de tienda». */
  etiqueta: string;
  /** Muestra «82.1 %» a la derecha. */
  mostrarPct?: boolean;
  tono?: TonoBarra;
  /** Alto de la pista en px (6 en escritorio, 8 en teléfono). */
  alto?: number;
  className?: string;
}

/** «82.1 %»: un decimal, punto decimal (es-MX), espacio antes del signo. */
export function pctAvance(fraccion: number | null): string {
  if (fraccion === null || !Number.isFinite(fraccion)) return '—';
  return `${(fraccion * 100).toLocaleString('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
}

/**
 * Barra de avance (la de «Suma capturada»). La pista se llena hasta 100 %;
 * si se pasa, la barra queda llena y el texto dice la cifra real (104.2 %).
 */
export const BarraAvance: React.FC<Props> = ({
  fraccion,
  etiqueta,
  mostrarPct = true,
  tono = 'primario',
  alto = 6,
  className,
}) => {
  const valido = fraccion !== null && Number.isFinite(fraccion);
  const ancho = valido ? Math.max(0, Math.min(1, fraccion as number)) * 100 : 0;
  const texto = pctAvance(fraccion);
  return (
    <div className={cx('flex items-center gap-2.5', className)}>
      <div
        role="progressbar"
        aria-label={etiqueta}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={valido ? Math.round(ancho * 10) / 10 : undefined}
        aria-valuetext={texto}
        className="flex-grow overflow-hidden bg-metas-pista"
        style={{ height: alto, borderRadius: alto / 2 }}
      >
        <div className={cx('h-full', TONO[tono])} style={{ width: `${ancho}%`, borderRadius: alto / 2 }} />
      </div>
      {mostrarPct && <span className="num text-xs font-semibold text-metas-texto-3">{texto}</span>}
    </div>
  );
};

export default BarraAvance;
